/**
 * Clue journal overlay (Tab) — collected clues with descriptions, plus the
 * suspect roster with accuse buttons. Owned by UI.
 */
import type { EventBus, Vec3 } from '../core/types'
import { emitAccusation } from './accusation'
import type { CaseLog } from './caseLog'
import {
  ACCUSE_BUTTON_LABEL,
  HIDDEN_CLASS,
  JOURNAL_CLUES_HEADING,
  JOURNAL_EMPTY_CLUES,
  JOURNAL_EMPTY_SUSPECTS,
  JOURNAL_FOOTER_HINT,
  JOURNAL_SUSPECTS_HEADING,
  JOURNAL_TITLE
} from './constants'
import { el } from './dom'

export interface Journal {
  setOpen(open: boolean): void
}

export const createJournal = (
  root: HTMLElement,
  bus: EventBus,
  caseLog: CaseLog,
  getPlayerPosition: () => Vec3
): Journal => {
  const overlay = el('section', `gum-panel gum-journal ${HIDDEN_CLASS}`)
  const columns = el('div', 'gum-journal-columns')
  const cluesColumn = el('div', 'gum-journal-column')
  const suspectsColumn = el('div', 'gum-journal-column')
  const cluesList = el('ul', 'gum-list')
  const suspectsList = el('ul', 'gum-list')

  cluesColumn.append(el('h3', 'gum-journal-heading', JOURNAL_CLUES_HEADING), cluesList)
  suspectsColumn.append(el('h3', 'gum-journal-heading', JOURNAL_SUSPECTS_HEADING), suspectsList)
  columns.append(cluesColumn, suspectsColumn)
  overlay.append(
    el('h2', 'gum-panel-title', JOURNAL_TITLE),
    columns,
    el('p', 'gum-panel-footer', JOURNAL_FOOTER_HINT)
  )
  root.append(overlay)

  const renderClues = (): void => {
    const clues = caseLog.clues()
    if (clues.length === 0) {
      cluesList.replaceChildren(el('li', 'gum-empty', JOURNAL_EMPTY_CLUES))
      return
    }
    cluesList.replaceChildren(
      ...clues.map((clue) => {
        const item = el('li', 'gum-clue')
        item.append(
          el('p', 'gum-clue-name', clue.name.toUpperCase()),
          el('p', 'gum-clue-desc', clue.description)
        )
        return item
      })
    )
  }

  const renderSuspects = (): void => {
    const suspects = caseLog.suspects()
    if (suspects.length === 0) {
      suspectsList.replaceChildren(el('li', 'gum-empty', JOURNAL_EMPTY_SUSPECTS))
      return
    }
    suspectsList.replaceChildren(
      ...suspects.map((suspect) => {
        const row = el('li', 'gum-suspect-row')
        const button = el('button', 'gum-button', ACCUSE_BUTTON_LABEL)
        button.type = 'button'
        button.addEventListener('click', () => {
          emitAccusation(bus, suspect.id, suspect.name, getPlayerPosition())
        })
        row.append(el('span', 'gum-suspect-name', suspect.name.toUpperCase()), button)
        return row
      })
    )
  }

  const render = (): void => {
    renderClues()
    renderSuspects()
  }

  caseLog.subscribe(render)
  render()

  return {
    setOpen: (open) => overlay.classList.toggle(HIDDEN_CLASS, !open)
  }
}
